import React from 'react'
import styles from './styles.module.css'
import HighlightedText from '../../../Utils/HighlightedText'
import CtaV2 from '../../../CtaV2'
import Img from '../../../Img'
import { $white } from '../../../../styles/colors/primitives'
import { sectionAccessibleName, ctaHiddenSuffix } from '../../../../utils/ctaAccessibleName'

function HomeSection({ imageCs, eyebrow, titleHl, textHl, ctasV2, bgColor, bgImage, marginTop, imageOnTop, useAspectRatio, useNaturalAspectRatio, noHorizontalMargin, isPageHero }) {
  // task #154: name generic CTA verbs ("Learn more"/"Buy"/"Pricing") by their
  // section/product so their accessible name is descriptive (Lighthouse link-text).
  const sectionName = sectionAccessibleName(titleHl)
  const getCtaSx = (cta) => {
    if (cta.transparent) {
      return { width: '120px', borderColor: $white, textColor: $white }
    }
    return { width: '120px' }
  }

  const renderImage = () => {
    if (!imageCs) return null

    if (useNaturalAspectRatio) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {/* task #67: render resolved descriptor via <Img> (natural/responsive sizing) */}
          <Img
            image={imageCs.image}
            layout="responsive"
            objectFit="contain"
            alt={imageCs.alt || ''}
            className="max-w-[calc(100%-64px)] rounded-2xl overflow-hidden"
          />
        </div>
      )
    }

    return (
      <div className={useAspectRatio ? styles.mainImageAspectRatio : styles.mainImage}>
        {/* task #67: render resolved descriptor via <Img>. reserveSpace={false} so the
            wrapper fills the parent box (.mainImage 700px / .mainImageAspectRatio 16:9)
            instead of self-sizing to the image aspect-ratio — the latter (post-#2a75f79d
            fill default) grows height = containerWidth/imageAR on this max-width-less,
            full-bleed container, oversizing the hero image at wide viewports. */}
        <Img
          image={imageCs.image}
          priority={true}
          alt={imageCs.alt}
          objectFit={useAspectRatio ? 'cover' : 'contain'}
          reserveSpace={false}
        />
      </div>
    )
  }

  // Single-<h1> rule (Audit A #1): the home page's ONE visible, keyword-rich <h1>
  // is the hero section's title. RenderSections marks the first non-hidden section
  // with `isPageHero`, so ONLY this section promotes its title to <h1> (the later
  // homeSection blocks on the same page stay non-heading). The <h1> uses
  // `display: contents` (styles.heroTitle) so it adds NO box — layout/visual are
  // byte-identical, the HighlightedText wrapper is still the flex item — while the
  // real, visible heading text becomes the document <h1> for SEO.
  const renderContent = () => (
    <div className="mx-auto w-full max-w-3xl text-center items-center justify-center flex flex-col gap-8">
      <div className="items-center justify-center flex flex-col gap-2">
        {eyebrow?.text && <HighlightedText {...eyebrow} />}
        {isPageHero
          ? <h1 className={styles.heroTitle}><HighlightedText {...titleHl} highlightExactMatch={true}/></h1>
          : <HighlightedText {...titleHl} highlightExactMatch={true}/>}
        <HighlightedText {...textHl} />
      </div>
      {ctasV2?.length > 0 && (
        <div className={styles.ctasWrapper}>
          { ctasV2.map((cta, i) => <CtaV2 {...cta} key={'cta-' + i} sx={getCtaSx(cta)} hiddenSuffix={ctaHiddenSuffix(cta.title, sectionName)} />) }
        </div>
      )}
    </div>
  )

  // Section: bgColor default var(--vrs-color-neutral-black), marginTop default 16px.
  const sectionStyle = {
    background: bgColor || 'var(--vrs-color-neutral-black)',
    marginTop: marginTop !== undefined ? `${marginTop}px` : '16px',
  }

  return (
    <div
      className={noHorizontalMargin ? `${styles.section} ${styles.sectionNoHorizontalMargin}` : styles.section}
      style={sectionStyle}
    >
      {/* task #67: styled bg fed the resolved descriptor url */}
      {bgImage?.image && (
        <div className={styles.backgroundImage} style={{ backgroundImage: `url(${bgImage?.image?.src})` }} />
      )}
      <div className={styles.contentWrapper}>
        {imageOnTop ? (
          <>
            {renderImage()}
            {renderContent()}
          </>
        ) : (
          <>
            {renderContent()}
            {renderImage()}
          </>
        )}
      </div>
    </div>
  )
}

export default HomeSection
